// Text links between a credit note and the invoice it credits (Oct 8 2026,
// approved "Not in design" text-only change). The member dashboard in
// cap-admin-console uses the same row wording (memberInvoiceCreditLinks.ts),
// and the backend PDF prints the same "Credited by invoice" document line.
// Keep the copies identical.

// Row link lines sit under the existing status text, one short line each.
// The Manage Invoices Status column has 124px for text below 1918px, so the
// approved one-line "Refunded · for #3689" and "Credited by #200, #233"
// would wrap. Each link is its own line instead and never wraps.

/** Credit note row link line, for example "For #101". */
export const getCreditNoteRowLinkLines = (
  creditForInvoiceNumber?: string | null,
): string[] => {
  const sourceNumber = creditForInvoiceNumber?.trim();
  return sourceNumber ? [`For #${sourceNumber}`] : [];
};

/** Source invoice row link lines, for example ["Credited by #200", "Credited by #233"]. */
export const getCreditedByRowLinkLines = (
  creditedByInvoiceNumbers?: readonly string[] | null,
): string[] =>
  (creditedByInvoiceNumbers || [])
    .map((invoiceNumber) => invoiceNumber.trim())
    .filter(Boolean)
    .map((invoiceNumber) => `Credited by #${invoiceNumber}`);

export type InvoiceCreditedByLink = Readonly<{
  creditedAmountInCents?: number | null;
  creditedAtUtcMilli?: number | null;
  invoiceId: string;
  invoiceNumber: string;
}>;

/**
 * Source invoice document line, for example
 * "Credited by invoice 200 on September 30, 2026 ($79.10)".
 */
export const getCreditedByDocumentLine = ({
  formatAmount,
  formatDate,
  link,
}: {
  formatAmount: (amountInCents: number) => string;
  formatDate: (utcMilli: number) => string;
  link: InvoiceCreditedByLink;
}): string =>
  [
    `Credited by invoice ${link.invoiceNumber.trim()}`,
    link.creditedAtUtcMilli ? ` on ${formatDate(link.creditedAtUtcMilli)}` : "",
    typeof link.creditedAmountInCents === "number"
      ? ` (${formatAmount(link.creditedAmountInCents)})`
      : "",
  ].join("");
